import { PartnerDealBriefingSectionResponse } from '@interfaces/partners/responses/partner-deal-briefing.response';

/**
 * Seleção de seções da aba Briefing (card 3878). Espelha o `BriefingSectionCatalog` do backend, que é quem decide de
 * verdade: obrigatória sempre entra, seção desligada é recusada e a ordem é a do catálogo. A tela só evita mandar um
 * pedido que o backend recusaria.
 *
 * Seletor "Formato e roteiro" (opção 2 do protótipo, escolhida pelo Victor em 04/11/2026): o formato Resumido marca
 * toda seção ligada; o Completo (12 páginas) chega na fase 2 e fica travado; o Personalizado libera os interruptores
 * das opcionais no roteiro do PDF.
 */

/**
 * Atalhos do backend (`BriefingSectionCatalog.Shortcut`). `summary` (Resumido) deixa de fora as opcionais de
 * propósito (`optIn`, a Investigação); `full` (Completo) marca todas as ligadas.
 */
export type BriefingShortcut = 'summary' | 'full';

/** Formato escolhido na tela: Resumido (`summary`) ou Personalizado (`custom`). O Completo não é escolhível na fase 1. */
export type BriefingFormat = 'summary' | 'custom';

/** Chave da seção que existe no catálogo mas começa desligada (chega na fase 2). */
export const BRIEFING_PROPOSED_STRUCTURE_KEY = 'proposed_structure';

/** Alvo de páginas do briefing resumido (régua do agente no backend). */
export const BRIEFING_TARGET_PAGES = 3;

/**
 * Peso aproximado de cada seção, em fração de página A4, para o roteiro mostrar onde a página provavelmente quebra e
 * o rodapé dizer "cerca de N páginas". É estimativa: o agente escreve o texto e ajusta as páginas no render. Valores
 * do protótipo aprovado; com as 7 seções da fase 1 dá 2,5 páginas, coerente com o alvo de até 3.
 */
export const BRIEFING_SECTION_PAGE_WEIGHTS: Readonly<Record<string, number>> = {
  header: 0.2,
  recommendation: 0.45,
  highlights: 0.3,
  pillars: 0.35,
  indicators: 0.45,
  red_flags: 0.45,
  next_steps: 0.3,
};

/** Peso de uma seção que o catálogo ganhar e esta tabela ainda não conhecer. */
const DEFAULT_SECTION_PAGE_WEIGHT = 0.35;

/** Folga para uma soma que passa um pouquinho da página (2,02) não virar uma página a mais. */
const PAGE_ROUNDING_TOLERANCE = 0.05;

/** Chave da seção da Investigação da empresa (card 3911), mãe das 11 subseções. */
export const BRIEFING_INVESTIGATION_KEY = 'investigation';

function byOrder(catalog: readonly PartnerDealBriefingSectionResponse[]) {
  return [...catalog].sort((a, b) => a.order - b.order);
}

/** Peso de página: o configurado nas regras (Q6) vence a tabela de fábrica. */
function sectionWeight(section: PartnerDealBriefingSectionResponse): number {
  return (
    section.pageWeight ?? BRIEFING_SECTION_PAGE_WEIGHTS[section.key] ?? DEFAULT_SECTION_PAGE_WEIGHT
  );
}

function isLockedWith(section: PartnerDealBriefingSectionResponse): boolean {
  return !!section.lockedWith?.length;
}

/** Subseções (card 3911) de uma seção mãe, na ordem do catálogo. */
export function childSections(
  parentKey: string,
  catalog: readonly PartnerDealBriefingSectionResponse[]
): PartnerDealBriefingSectionResponse[] {
  return byOrder(catalog).filter(s => s.parentKey === parentKey);
}

/** A seção é anexo (ela ou a mãe dela): fica fora do teto de páginas do agente. */
function isAnnex(
  section: PartnerDealBriefingSectionResponse,
  catalog: readonly PartnerDealBriefingSectionResponse[]
): boolean {
  if (section.annex) return true;
  const parent = section.parentKey ? catalog.find(s => s.key === section.parentKey) : null;
  return !!parent?.annex;
}

/**
 * Catálogo de quem não tem `PARTNERDEALBRIEFINGINVESTIGATION_RUN` (card 3911): a Investigação e as subseções dela
 * ficam desligadas, então o seletor mostra a linha travada e a seleção nunca as inclui. Sem a Investigação no
 * catálogo, devolve o mesmo array.
 */
export function withoutInvestigation(
  catalog: readonly PartnerDealBriefingSectionResponse[]
): readonly PartnerDealBriefingSectionResponse[] {
  const isInvestigation = (s: PartnerDealBriefingSectionResponse) =>
    s.key === BRIEFING_INVESTIGATION_KEY || s.parentKey === BRIEFING_INVESTIGATION_KEY;
  if (!catalog.some(isInvestigation)) return catalog;
  return catalog.map(s => (isInvestigation(s) ? { ...s, enabled: false } : s));
}

/**
 * As chaves selecionadas na ordem do catálogo, com as obrigatórias ligadas sempre presentes e sem repetição. Espelha
 * o `NormalizeSelection` do backend (card 3911): subseção sem a mãe traz a mãe; mãe sem subseção traz as subseções
 * ligadas; a subseção presa (`lockedWith`, a Recomendação da investigação) entra quando alguma das dela entra e sai
 * quando nenhuma entra; desligada nunca entra.
 */
export function normalizeSelection(
  selected: readonly string[],
  catalog: readonly PartnerDealBriefingSectionResponse[]
): string[] {
  const enabled = byOrder(catalog).filter(s => s.enabled);
  const enabledKeys = new Set(enabled.map(s => s.key));
  const picked = new Set(selected.filter(key => enabledKeys.has(key)));

  for (const child of enabled) {
    if (child.parentKey && picked.has(child.key) && enabledKeys.has(child.parentKey)) {
      picked.add(child.parentKey);
    }
  }
  for (const parent of enabled) {
    const children = enabled.filter(s => s.parentKey === parent.key);
    if (!children.length) continue;
    if (!picked.has(parent.key)) {
      children.forEach(child => picked.delete(child.key));
      continue;
    }
    if (!children.some(child => !isLockedWith(child) && picked.has(child.key))) {
      children.forEach(child => picked.add(child.key));
    }
  }
  for (const section of enabled) {
    if (!isLockedWith(section)) continue;
    const anyPicked = (section.lockedWith ?? []).some(key => picked.has(key));
    if (anyPicked) picked.add(section.key);
    else picked.delete(section.key);
  }

  return enabled.filter(s => s.required || picked.has(s.key)).map(s => s.key);
}

/**
 * Atalho: Resumido marca toda seção ligada, menos as opcionais de propósito (`optIn`, como a Investigação) e as
 * subseções delas; Completo marca todas as ligadas. Seção desligada (ex.: Estrutura proposta) nunca entra.
 */
export function applyShortcut(
  catalog: readonly PartnerDealBriefingSectionResponse[],
  shortcut: BriefingShortcut
): string[] {
  // Atalho desconhecido (link velho, valor fora do tipo) não marca nada além das obrigatórias.
  if (shortcut !== 'summary' && shortcut !== 'full') {
    return normalizeSelection([], catalog);
  }
  const optInKeys = new Set(catalog.filter(s => s.optIn).map(s => s.key));
  const keys = byOrder(catalog)
    .filter(s => s.enabled)
    .filter(
      s =>
        shortcut === 'full' ||
        (!optInKeys.has(s.key) && !(s.parentKey && optInKeys.has(s.parentKey)))
    )
    .map(s => s.key);
  return normalizeSelection(keys, catalog);
}

/**
 * Marca ou desmarca uma seção. Obrigatória e subseção presa não mudam sozinhas, desligada não entra e chave fora do
 * catálogo não muda nada. Mãe: liga com as subseções ligadas e desliga com todas. Subseção: desmarcar a última tira
 * a mãe (senão o backend traria todas de volta). Devolve sempre uma lista nova, na ordem do catálogo.
 */
export function toggleSection(
  selected: readonly string[],
  key: string,
  catalog: readonly PartnerDealBriefingSectionResponse[]
): string[] {
  const section = catalog.find(s => s.key === key);
  if (!section || section.required || !section.enabled || isLockedWith(section)) {
    return normalizeSelection(selected, catalog);
  }

  const isOn = isSectionSelected(section, selected);
  let next = isOn ? selected.filter(k => k !== key) : [...selected, key];

  if (section.parentKey && isOn) {
    const siblingsLeft = childSections(section.parentKey, catalog).filter(
      child => child.enabled && !isLockedWith(child) && next.includes(child.key)
    );
    if (!siblingsLeft.length) next = next.filter(k => k !== section.parentKey);
  }
  if (!section.parentKey && isOn) {
    const children = new Set(childSections(key, catalog).map(child => child.key));
    next = next.filter(k => !children.has(k));
  }
  return normalizeSelection(next, catalog);
}

/** Se a seção está marcada no briefing que vai ser pedido (obrigatória ligada conta sempre). */
export function isSectionSelected(
  section: PartnerDealBriefingSectionResponse,
  selected: readonly string[]
): boolean {
  return section.enabled && (section.required || selected.includes(section.key));
}

/** Estimativa de tamanho do briefing com as seções marcadas. */
export interface BriefingPageEstimate {
  /** Soma dos pesos do corpo do briefing, em páginas (ex.: 2,5). */
  pages: number;
  /** Páginas inteiras que o rodapé mostra ("cerca de N páginas"), no mínimo 1. */
  pageCount: number;
  /** A estimativa do corpo passa do alvo de até 3 páginas (aviso, não bloqueio). */
  overTarget: boolean;
  /** Páginas inteiras do anexo (Investigação), fora do alvo; 0 sem anexo. */
  annexPageCount: number;
}

function roundUpPages(pages: number): number {
  return Math.max(1, Math.ceil(pages - PAGE_ROUNDING_TOLERANCE));
}

export function estimatePages(
  selected: readonly string[],
  catalog: readonly PartnerDealBriefingSectionResponse[]
): BriefingPageEstimate {
  let pages = 0;
  let annexPages = 0;
  for (const section of byOrder(catalog)) {
    if (!isSectionSelected(section, selected)) continue;
    if (isAnnex(section, catalog)) annexPages += sectionWeight(section);
    else pages += sectionWeight(section);
  }
  const pageCount = roundUpPages(pages);
  return {
    pages,
    pageCount,
    overTarget: pageCount > BRIEFING_TARGET_PAGES,
    annexPageCount: annexPages > 0 ? roundUpPages(annexPages) : 0,
  };
}

/** Estado de uma linha do roteiro. */
export type BriefingRoadmapState = 'always' | 'in' | 'out' | 'locked';

/** Subseção dentro de uma linha do roteiro (card 3911). `auto` = vai junto com outras (não se marca sozinha). */
export interface BriefingRoadmapChild {
  key: string;
  name: string;
  state: 'in' | 'out' | 'auto' | 'locked';
}

/** Uma linha do "Roteiro do PDF", na ordem em que a seção sai no documento. */
export interface BriefingRoadmapRow {
  key: string;
  /** Nome sem o complemento entre parênteses ("Destaques"). */
  name: string;
  /** Complemento do título do catálogo ("5 cards"), quando houver. */
  detail: string | null;
  state: BriefingRoadmapState;
  /** Posição da seção no documento (1, 2, 3...), só nas que entram. */
  position: number | null;
  /** Quando a seção provavelmente começa numa página nova, o número dela; senão nulo. */
  startsPage: number | null;
  /** Primeira seção de anexo: o roteiro põe o separador "Anexo" antes dela. */
  startsAnnex: boolean;
  /** Subseções (só na mãe, ex.: Investigação); vazio nas demais. */
  children: BriefingRoadmapChild[];
}

const TITLE_DETAIL = /^(.*?)\s*\(([^)]+)\)\s*$/;

function splitTitle(title: string): { name: string; detail: string | null } {
  const match = TITLE_DETAIL.exec(title ?? '');
  return match ? { name: match[1], detail: match[2] } : { name: title, detail: null };
}

/**
 * Monta o roteiro do PDF: todas as seções do catálogo na ordem do documento, com a numeração das que entram e o
 * separador de página onde a soma dos pesos cruza um número inteiro. A seção que começa numa página nova é a que
 * leva o separador; é estimativa simples, sem prometer exatidão.
 */
export function buildRoadmap(
  selected: readonly string[],
  catalog: readonly PartnerDealBriefingSectionResponse[]
): BriefingRoadmapRow[] {
  let position = 0;
  let accumulated = 0;
  let lastPage = 1;
  let annexStarted = false;

  return byOrder(catalog)
    .filter(section => !section.parentKey)
    .map(section => {
      const { name, detail } = splitTitle(section.title);
      const included = isSectionSelected(section, selected);
      const state: BriefingRoadmapState = !section.enabled
        ? 'locked'
        : section.required
          ? 'always'
          : included
            ? 'in'
            : 'out';
      const children: BriefingRoadmapChild[] = childSections(section.key, catalog).map(child => ({
        key: child.key,
        name: splitTitle(child.title).name,
        state: !child.enabled
          ? 'locked'
          : isLockedWith(child)
            ? 'auto'
            : isSectionSelected(child, selected)
              ? 'in'
              : 'out',
      }));
      const annex = isAnnex(section, catalog);
      const startsAnnex = annex && !annexStarted;
      if (annex) annexStarted = true;

      if (!included) {
        return {
          key: section.key,
          name,
          detail,
          state,
          position: null,
          startsPage: null,
          startsAnnex,
          children,
        };
      }

      position++;
      // O anexo não entra na conta das páginas do corpo (fica fora do teto do agente).
      if (annex) {
        return {
          key: section.key,
          name,
          detail,
          state,
          position,
          startsPage: null,
          startsAnnex,
          children,
        };
      }
      const page = Math.floor(accumulated + 1e-9) + 1;
      const startsPage = page > lastPage ? page : null;
      lastPage = page;
      accumulated += sectionWeight(section);
      return { key: section.key, name, detail, state, position, startsPage, startsAnnex, children };
    });
}
